import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import {
  useCallback, useEffect, useMemo, useState
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { Button } from '@/components/button/button';
import CodeSelect from '@/components/codeSelect';
import Modal from '@/components/modal/modal';
import { Heading } from '@/components/typography/heading';
import { Text } from '@/components/typography/text';
import { useCreateApiKey } from '@/hooks/mutations/useApiKeyMutations';
import { useAuthContext } from '@/hooks/useAuthContext';
import {
  ApiKeyType,
  ResolvedScope,
  ResourceKind,
  ScopeRow
} from '@/types/apiKey';
import {
  emptyScopeRow, rowHasAnyPermission, rowIsValid, rowsToResolvedScope
} from '@/utils/apiKeyScope';
import { extractApiErrorMessage } from '@/utils/extractApiErrorMessage';

import styles from './apiKeysPanel.module.scss';
import { ScopeBuilder } from './scopeBuilder';
import { ScopeSummary } from './scopeSummary';


const SCOPE_KINDS: ResourceKind[] = [ 'constellation', 'secret', 'evaluation' ];

const SELECTABLE_KEY_TYPES = [ 'personal', 'shared' ] as const satisfies readonly ApiKeyType[];

type Step = 'details' | 'scope' | 'review' | 'show-key';

const formSchema = z.object( {
  name: z.string().trim().min( 1, 'Name is required' ),
  type: z.enum( SELECTABLE_KEY_TYPES ),
  mirror: z.boolean()
} );

type FormData = z.infer<typeof formSchema>;

type CreateKeyModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function CreateKeyModal( { isOpen, onClose }: CreateKeyModalProps ) {
  const { canAccess } = useAuthContext();
  // can_create_api_keys — required to create any key
  // can_create_shared_api_keys — additionally required for shared keys (admin only)
  const canCreateSharedKey = canAccess( 'can_create_shared_api_keys' );
  const createMutation = useCreateApiKey();

  const [ step, setStep ] = useState<Step>( 'details' );
  const [ rows, setRows ] = useState<ScopeRow[]>( () => SCOPE_KINDS.map( emptyScopeRow ) );
  const [ submitError, setSubmitError ] = useState<string | null>( null );
  const [ createdRawKey, setCreatedRawKey ] = useState<string>( '' );

  const {
    control, register, watch, handleSubmit, reset, setValue,
    formState: { errors }
  } = useForm<FormData>( {
    resolver: zodResolver( formSchema ),
    mode: 'onChange',
    defaultValues: {
      name: '',
      type: 'personal',
      mirror: false
    }
  } );

  const watchedType = watch( 'type' );
  const watchedMirror = watch( 'mirror' );
  const watchedName = watch( 'name' );

  useEffect( () => {
    if ( !isOpen ) return;
    setStep( 'details' );
    setRows( SCOPE_KINDS.map( emptyScopeRow ) );
    setSubmitError( null );
    setCreatedRawKey( '' );
    reset( { name: '', type: 'personal', mirror: false } );
  }, [ isOpen, reset ] );

  // mirror is only valid for personal keys, not shared
  useEffect( () => {
    if ( watchedType !== 'personal' && watchedMirror ) {
      setValue( 'mirror', false );
    }
  }, [ watchedType, watchedMirror, setValue ] );

  const usesMatrix = !watchedMirror;

  const matrixHasValidScope = useMemo( () => {
    if ( !usesMatrix ) return true;
    const populated = rows.filter( rowHasAnyPermission );
    if ( populated.length === 0 ) return false;
    return populated.every( rowIsValid );
  }, [ rows, usesMatrix ] );

  const detailsValid = !errors.name && watchedName.trim().length > 0
    && ( watchedType !== 'shared' || canCreateSharedKey );

  const resolvedScope: ResolvedScope = useMemo(
    () => rowsToResolvedScope( rows, { mirror: watchedMirror, fullAccess: false } ),
    [ rows, watchedMirror ]
  );

  const onNext = useCallback( () => {
    if ( step === 'details' ) setStep( 'scope' );
    else if ( step === 'scope' ) setStep( 'review' );
  }, [ step ] );

  const onBack = useCallback( () => {
    if ( step === 'scope' ) setStep( 'details' );
    else if ( step === 'review' ) setStep( 'scope' );
  }, [ step ] );

  const onCreate = useCallback( handleSubmit( async( data ) => {
    setSubmitError( null );
    try {
      const res = await createMutation.mutateAsync( {
        name: data.name.trim(),
        type: data.type,
        scope: resolvedScope
      } );
      setCreatedRawKey( res.key );
      setStep( 'show-key' );
    } catch( err ) {
      setSubmitError( extractApiErrorMessage( err ) );
    }
  } ), [ handleSubmit, createMutation, resolvedScope ] );

  if ( !isOpen ) return null;

  return (
    <Modal className={ styles.modal }>
      <div className={ styles.modal_content }>
        <Heading as="h2" size="md">
          { match( step )
            .with( 'details', () => 'Create API Key' )
            .with( 'scope', () => 'Create API Key — Scope' )
            .with( 'review', () => 'Create API Key — Review' )
            .with( 'show-key', () => 'Your New API Key' )
            .exhaustive() }
        </Heading>

        <div className={ styles.modal_body }>
          { step === 'details' && (
            <>
              <div className={ styles.form_field }>
                <label className={ styles.field_label } htmlFor="api-key-name">Name</label>
                <input
                  id="api-key-name"
                  type="text"
                  className={ styles.input_field }
                  data-testid="api-key-name-input"
                  { ...register( 'name' ) }
                />
                { errors.name && <span className={ styles.validation_error }>{ errors.name.message }</span> }
              </div>

              <div className={ styles.form_field }>
                <span className={ styles.field_label }>Type</span>
                <Controller
                  control={ control }
                  name="type"
                  render={ ( { field } ) => (
                    <div className={ styles.radio_group }>
                      { SELECTABLE_KEY_TYPES.map( ( t ) => {
                        const disabled = t === 'shared' && !canCreateSharedKey;
                        return (
                          <label
                            key={ t }
                            className={ clsx( styles.radio_label, disabled && styles.radio_disabled ) }
                            title={ disabled ? 'You don\'t have permission to create Shared keys' : undefined }
                          >
                            <input
                              type="radio"
                              name={ field.name }
                              value={ t }
                              checked={ field.value === t }
                              disabled={ disabled }
                              onChange={ () => field.onChange( t ) }
                              data-testid={ `api-key-type-${ t }` }
                            />
                            <span>{ t === 'personal' ? 'Personal' : 'Shared' }</span>
                          </label>
                        );
                      } ) }
                    </div>
                  ) }
                />
                <Text size="sm" className={ styles.helper }>
                  { watchedType === 'personal'
                    ? 'Tied to your user identity. Auto-revoked if your account is deleted.'
                    : 'Org-owned. Use for production or CI integrations.' }
                </Text>
              </div>
            </>
          ) }

          { step === 'scope' && (
            <>
              { watchedType === 'personal' && (
                <label className={ styles.toggle_row }>
                  <input
                    type="checkbox"
                    { ...register( 'mirror' ) }
                    data-testid="api-key-mirror-toggle"
                  />
                  <div>
                    <div className={ styles.toggle_title }>Mirror my access</div>
                    <Text size="sm" className={ styles.helper }>
                      This key will mirror your current access, including your tenant role.
                      If your access changes later, this key&apos;s scope does not.
                    </Text>
                  </div>
                </label>
              ) }

              { usesMatrix && (
                <ScopeBuilder rows={ rows } onChange={ setRows } />
              ) }
            </>
          ) }

          { step === 'review' && (
            <>
              <div className={ styles.review_row }>
                <span className={ styles.review_label }>Name</span>
                <span>{ watchedName }</span>
              </div>
              <div className={ styles.review_row }>
                <span className={ styles.review_label }>Type</span>
                <span>{ watchedType === 'personal' ? 'Personal' : 'Shared' }</span>
              </div>
              <div className={ styles.review_row }>
                <span className={ styles.review_label }>Scope</span>
                <ScopeSummary scope={ resolvedScope } />
              </div>
              { submitError && (
                <div className={ styles.validation_error }>{ submitError }</div>
              ) }
            </>
          ) }

          { step === 'show-key' && (
            <>
              <Text size="md">
                Save this key now — you will not be able to view it again after closing this dialog.
              </Text>
              <div className={ styles.key_box }>
                <CodeSelect value={ createdRawKey } />
              </div>
            </>
          ) }
        </div>

        <div className={ styles.modal_footer }>
          { step === 'show-key' ? (
            <Button variant="primary" size="sm" onClick={ onClose }>Close</Button>
          ) : (
            <>
              <Button variant="secondary" size="sm" onClick={ onClose } type="button">Cancel</Button>
              { step !== 'details' && (
                <Button variant="secondary" size="sm" onClick={ onBack } type="button">Back</Button>
              ) }
              { step === 'review' ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={ onCreate }
                  disabled={ createMutation.isPending }
                  loading={ createMutation.isPending }
                  data-testid="api-key-create-submit"
                >
                  Create
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={ onNext }
                  disabled={
                    ( step === 'details' && !detailsValid )
                    || ( step === 'scope' && !matrixHasValidScope )
                  }
                  data-testid="api-key-next-step"
                >
                  Next
                </Button>
              ) }
            </>
          ) }
        </div>
      </div>
    </Modal>
  );
}
